import {
  Product,
  Collection,
  Cart,
  Customer,
  Order,
  OrderLineItem,
  Address,
  CustomerAccessToken,
  ShopifyProduct,
  ShopifyCollection,
  ShopifyCart,
  ShopifyCustomer,
  ShopifyOrder,
  ShopifyResponse,
  ProductsResponse,
  ProductResponse,
  CollectionsResponse,
  CollectionResponse,
  CartResponse,
  CartCreateResponse,
  CartLinesAddResponse,
  CartLinesUpdateResponse,
  CartLinesRemoveResponse,
  CustomerAccessTokenCreateResponse,
  CustomerCreateResponse,
  CustomerResponse,
  CustomerUserError,
  Money,
  Image,
} from './types';

import {
  SHOPIFY_STORE_DOMAIN_LOCAL,
  SHOPIFY_STOREFRONT_ACCESS_TOKEN_LOCAL,
} from './config.local';
import { getSeoRuntimeCollectionDefinition } from '@/lib/seo-runtime-categories';

const domain = process.env.SHOPIFY_STORE_DOMAIN || SHOPIFY_STORE_DOMAIN_LOCAL;
const storefrontAccessToken =
  process.env.SHOPIFY_STOREFRONT_ACCESS_TOKEN || SHOPIFY_STOREFRONT_ACCESS_TOKEN_LOCAL;
const apiVersion = '2024-01';
const isBunnyStaticExport = process.env.BUNNY_STATIC_EXPORT === 'true';

const endpoint = `https://${domain}/api/${apiVersion}/graphql.json`;

const PRODUCT_FRAGMENT = `
  fragment ProductFragment on Product {
    id
    handle
    title
    description
    descriptionHtml
    availableForSale
    updatedAt
    featuredImage {
      url
      altText
      width
      height
    }
    images(first: 10) {
      edges {
        node {
          url
          altText
          width
          height
        }
      }
    }
    options {
      id
      name
      values
    }
    priceRange {
      minVariantPrice {
        amount
        currencyCode
      }
      maxVariantPrice {
        amount
        currencyCode
      }
    }
    variants(first: 100) {
      edges {
        node {
          id
          title
          sku
          availableForSale
          selectedOptions {
            name
            value
          }
          price {
            amount
            currencyCode
          }
          compareAtPrice {
            amount
            currencyCode
          }
          image {
            url
            altText
            width
            height
          }
        }
      }
    }
    tags
    productType
    vendor
    seo {
      title
      description
    }
  }
`;

const COLLECTION_FRAGMENT = `
  fragment CollectionFragment on Collection {
    id
    handle
    title
    description
    image {
      url
      altText
      width
      height
    }
    seo {
      title
      description
    }
  }
`;

const CART_FRAGMENT = `
  fragment CartFragment on Cart {
    id
    checkoutUrl
    cost {
      subtotalAmount {
        amount
        currencyCode
      }
      totalAmount {
        amount
        currencyCode
      }
      totalTaxAmount {
        amount
        currencyCode
      }
    }
    lines(first: 100) {
      edges {
        node {
          id
          quantity
          attributes {
            key
            value
          }
          cost {
            totalAmount {
              amount
              currencyCode
            }
          }
          merchandise {
            ... on ProductVariant {
              id
              title
              selectedOptions {
                name
                value
              }
              product {
                id
                handle
                title
                featuredImage {
                  url
                  altText
                  width
                  height
                }
              }
              price {
                amount
                currencyCode
              }
            }
          }
        }
      }
    }
    totalQuantity
  }
`;

async function shopifyFetch<T>({
  query,
  variables,
  cache = 'no-store',
  tags,
}: {
  query: string;
  variables?: Record<string, unknown>;
  cache?: RequestCache;
  tags?: string[];
}): Promise<T> {
  const fetchCache =
    isBunnyStaticExport && cache === 'no-store' ? 'force-cache' : cache;

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
    },
    body: JSON.stringify({ query, variables }),
    cache: fetchCache,
    ...(!isBunnyStaticExport && tags && { next: { tags } }),
  });

  if (!response.ok) {
    throw new Error(`Shopify API error: ${response.status} ${response.statusText}`);
  }

  const json: ShopifyResponse<T> = await response.json();

  if (json.errors) {
    console.error('Shopify GraphQL Errors:', json.errors);
    throw new Error(json.errors[0].message);
  }

  return json.data;
}

function reshapeProduct(product: ShopifyProduct): Product {
  return {
    ...product,
    images: product.images.edges.map((edge) => edge.node),
    variants: product.variants.edges.map((edge) => edge.node),
  };
}

function reshapeCollection(collection: ShopifyCollection): Collection {
  return {
    ...collection,
    products: collection.products?.edges.map((edge) => reshapeProduct(edge.node)),
  };
}

function reshapeCart(cart: ShopifyCart): Cart {
  return {
    ...cart,
    lines: cart.lines.edges.map((edge) => edge.node),
  };
}

export async function getProducts(first: number = 20): Promise<Product[]> {
  const query = `
    ${PRODUCT_FRAGMENT}
    query GetProducts($first: Int!) {
      products(first: $first) {
        edges {
          node {
            ...ProductFragment
          }
        }
      }
    }
  `;

  const data = await shopifyFetch<ProductsResponse>({
    query,
    variables: { first },
    tags: ['products'],
  });

  return data.products.edges.map((edge) => reshapeProduct(edge.node));
}

export interface ProductSitemapEntry {
  handle: string;
  updatedAt?: string;
}

interface ProductSitemapResponse {
  products: {
    edges: { node: { handle: string; updatedAt?: string } }[];
    pageInfo: { hasNextPage: boolean; endCursor: string | null };
  };
}

/**
 * Lightweight collections-with-preview fetch used by the header mega menu.
 * Includes up to 6 product previews (handle, title, featuredImage, min price)
 * per collection — no full product shape, no variants.
 */
export async function getCollectionsWithPreviews(): Promise<Collection[]> {
  const query = `
    query CollectionsWithPreviews {
      collections(first: 100) {
        edges {
          node {
            id
            handle
            title
            description
            image { url altText width height }
            seo { title description }
            products(first: 6) {
              edges {
                node {
                  id
                  handle
                  title
                  featuredImage { url altText width height }
                  priceRange {
                    minVariantPrice { amount currencyCode }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;
  try {
    const data = await shopifyFetch<{
      collections: {
        edges: {
          node: {
            id: string;
            handle: string;
            title: string;
            description: string;
            image: Image | null;
            seo: { title: string | null; description: string | null };
            products: {
              edges: {
                node: {
                  id: string;
                  handle: string;
                  title: string;
                  featuredImage: Image | null;
                  priceRange: { minVariantPrice: Money };
                };
              }[];
            };
          };
        }[];
      };
    }>({ query, tags: ['collections'] });

    return data.collections.edges.map((edge) => {
      const c = edge.node;
      return {
        id: c.id,
        handle: c.handle,
        title: c.title,
        description: c.description,
        image: c.image,
        seo: c.seo,
        products: c.products.edges.map((pe) => ({
          id: pe.node.id,
          handle: pe.node.handle,
          title: pe.node.title,
          description: '',
          descriptionHtml: '',
          availableForSale: true,
          featuredImage: pe.node.featuredImage,
          images: pe.node.featuredImage ? [pe.node.featuredImage] : [],
          options: [],
          priceRange: {
            minVariantPrice: pe.node.priceRange.minVariantPrice,
            maxVariantPrice: pe.node.priceRange.minVariantPrice,
          },
          variants: [],
          tags: [],
          productType: '',
          vendor: '',
          seo: { title: null, description: null },
        })),
      } as Collection;
    });
  } catch {
    return [];
  }
}

export async function getAllProductsForSitemap(): Promise<ProductSitemapEntry[]> {
  const query = `
    query SitemapProducts($first: Int!, $after: String) {
      products(first: $first, after: $after) {
        edges {
          cursor
          node {
            handle
            updatedAt
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  const entries: ProductSitemapEntry[] = [];
  let after: string | null = null;

  for (let page = 0; page < 40; page++) {
    const data: ProductSitemapResponse = await shopifyFetch<ProductSitemapResponse>({
      query,
      variables: { first: 250, after },
      tags: ['products'],
    });

    for (const edge of data.products.edges) {
      entries.push({ handle: edge.node.handle, updatedAt: edge.node.updatedAt });
    }

    if (!data.products.pageInfo.hasNextPage) break;
    after = data.products.pageInfo.endCursor;
  }

  return entries;
}

export async function getProduct(handle: string): Promise<Product | null> {
  const query = `
    ${PRODUCT_FRAGMENT}
    query GetProduct($handle: String!) {
      product(handle: $handle) {
        ...ProductFragment
      }
    }
  `;

  const data = await shopifyFetch<ProductResponse>({
    query,
    variables: { handle },
    tags: ['products', `product-${handle}`],
  });

  return data.product ? reshapeProduct(data.product) : null;
}

export async function getProductRecommendations(productId: string): Promise<Product[]> {
  const query = `
    ${PRODUCT_FRAGMENT}
    query Recommendations($productId: ID!) {
      productRecommendations(productId: $productId) {
        ...ProductFragment
      }
    }
  `;

  try {
    const data = await shopifyFetch<{ productRecommendations: ShopifyProduct[] | null }>({
      query,
      variables: { productId },
      tags: ['products', `recs-${productId}`],
    });
    const recs = data.productRecommendations ?? [];
    return recs.map(reshapeProduct);
  } catch {
    return [];
  }
}

export async function predictiveSearch(searchQuery: string, limit: number = 6): Promise<Product[]> {
  const query = `
    query PredictiveSearch($query: String!, $limit: Int!) {
      predictiveSearch(query: $query, limit: $limit, types: PRODUCT) {
        products {
          id
          handle
          title
          availableForSale
          featuredImage {
            url
            altText
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          variants(first: 1) {
            edges {
              node {
                id
                title
                availableForSale
                selectedOptions {
                  name
                  value
                }
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
              }
            }
          }
          options {
            id
            name
            values
          }
          tags
          productType
          vendor
          description
          descriptionHtml
          images(first: 1) {
            edges {
              node {
                url
                altText
                width
                height
              }
            }
          }
          seo {
            title
            description
          }
        }
      }
    }
  `;

  const data = await shopifyFetch<{
    predictiveSearch: { products: ShopifyProduct[] };
  }>({
    query,
    variables: { query: searchQuery, limit },
    cache: 'no-store',
  });

  return data.predictiveSearch.products.map((p) => reshapeProduct(p));
}

export async function searchProducts(searchQuery: string, first: number = 50): Promise<Product[]> {
  const query = `
    ${PRODUCT_FRAGMENT}
    query SearchProducts($query: String!, $first: Int!) {
      search(query: $query, first: $first, types: PRODUCT) {
        edges {
          node {
            ... on Product {
              ...ProductFragment
            }
          }
        }
      }
    }
  `;

  const data = await shopifyFetch<{
    search: { edges: { node: ShopifyProduct }[] };
  }>({
    query,
    variables: { query: searchQuery, first },
    cache: 'no-store',
  });

  return data.search.edges.map((edge) => reshapeProduct(edge.node));
}

export async function getCollections(): Promise<Collection[]> {
  const query = `
    ${COLLECTION_FRAGMENT}
    ${PRODUCT_FRAGMENT}
    query GetCollections {
      collections(first: 100) {
        edges {
          node {
            ...CollectionFragment
            products(first: 100) {
              edges {
                node {
                  ...ProductFragment
                }
              }
            }
          }
        }
      }
    }
  `;

  const data = await shopifyFetch<CollectionsResponse>({
    query,
    cache: 'no-store',
  });

  return data.collections.edges.map((edge) => reshapeCollection(edge.node));
}

const PRODUCT_HANDLE_BATCH_SIZE = 35;

async function getProductsByHandles(handles: readonly string[]): Promise<Product[]> {
  const uniqueHandles = Array.from(new Set(handles));
  const products: Product[] = [];

  for (let index = 0; index < uniqueHandles.length; index += PRODUCT_HANDLE_BATCH_SIZE) {
    const batch = uniqueHandles.slice(index, index + PRODUCT_HANDLE_BATCH_SIZE);
    const query = `
      ${PRODUCT_FRAGMENT}
      query SeoRuntimeProducts {
        ${batch
          .map((handle, batchIndex) => {
            return `p${batchIndex}: product(handle: ${JSON.stringify(handle)}) { ...ProductFragment }`;
          })
          .join('\n')}
      }
    `;

    const data = await shopifyFetch<Record<string, ShopifyProduct | null>>({
      query,
      cache: 'no-store',
    });

    for (let batchIndex = 0; batchIndex < batch.length; batchIndex += 1) {
      const product = data[`p${batchIndex}`];
      if (product) products.push(reshapeProduct(product));
    }
  }

  const byHandle = new Map(
    products
      .map((product) => [product.handle, product])
  );

  return handles
    .map((handle) => byHandle.get(handle))
    .filter((product): product is Product => Boolean(product));
}

async function getSeoRuntimeCollection(handle: string): Promise<Collection | null> {
  const definition = getSeoRuntimeCollectionDefinition(handle);
  if (!definition) return null;

  const products = await getProductsByHandles(definition.productHandles);
  const image = products.find((product) => product.featuredImage)?.featuredImage ?? null;

  return {
    id: `seo-runtime:${definition.handle}`,
    handle: definition.handle,
    title: definition.title,
    description: definition.description,
    image,
    products,
    seo: {
      title: definition.title,
      description: definition.description,
    },
  };
}

export async function getCollection(handle: string): Promise<Collection | null> {
  const seoRuntimeCollection = await getSeoRuntimeCollection(handle);
  if (seoRuntimeCollection) return seoRuntimeCollection;

  const firstPageQuery = `
    ${COLLECTION_FRAGMENT}
    ${PRODUCT_FRAGMENT}
    query GetCollection($handle: String!) {
      collection(handle: $handle) {
        ...CollectionFragment
        products(first: 250) {
          edges {
            node {
              ...ProductFragment
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  `;

  const data = await shopifyFetch<CollectionResponse>({
    query: firstPageQuery,
    variables: { handle },
    cache: 'no-store',
  });

  if (!data.collection) return null;

  const collection = data.collection;
  let cursor = collection.products?.pageInfo?.endCursor ?? null;
  let hasNext = collection.products?.pageInfo?.hasNextPage ?? false;

  const nextPageQuery = `
    ${PRODUCT_FRAGMENT}
    query GetCollectionProductsPage($handle: String!, $after: String!) {
      collection(handle: $handle) {
        products(first: 250, after: $after) {
          edges {
            node {
              ...ProductFragment
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  `;

  while (hasNext && cursor) {
    const pageData = await shopifyFetch<CollectionResponse>({
      query: nextPageQuery,
      variables: { handle, after: cursor },
      cache: 'no-store',
    });

    const pageProducts = pageData.collection?.products;
    if (!pageProducts) break;

    collection.products!.edges.push(...pageProducts.edges);
    cursor = pageProducts.pageInfo?.endCursor ?? null;
    hasNext = pageProducts.pageInfo?.hasNextPage ?? false;
  }

  return reshapeCollection(collection);
}

export async function createCart(): Promise<Cart> {
  const query = `
    ${CART_FRAGMENT}
    mutation CreateCart {
      cartCreate {
        cart {
          ...CartFragment
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const data = await shopifyFetch<CartCreateResponse>({
    query,
    cache: 'no-store',
  });

  if (data.cartCreate.userErrors.length > 0) {
    throw new Error(data.cartCreate.userErrors[0].message);
  }

  return reshapeCart(data.cartCreate.cart);
}

export async function getCart(cartId: string): Promise<Cart | null> {
  const query = `
    ${CART_FRAGMENT}
    query GetCart($cartId: ID!) {
      cart(id: $cartId) {
        ...CartFragment
      }
    }
  `;

  const data = await shopifyFetch<CartResponse>({
    query,
    variables: { cartId },
    cache: 'no-store',
  });

  return data.cart ? reshapeCart(data.cart) : null;
}

export async function addToCart(
  cartId: string,
  lines: { merchandiseId: string; quantity: number; attributes?: { key: string; value: string }[] }[]
): Promise<Cart> {
  const query = `
    ${CART_FRAGMENT}
    mutation AddToCart($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart {
          ...CartFragment
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const data = await shopifyFetch<CartLinesAddResponse>({
    query,
    variables: { cartId, lines },
    cache: 'no-store',
  });

  if (data.cartLinesAdd.userErrors.length > 0) {
    throw new Error(data.cartLinesAdd.userErrors[0].message);
  }

  return reshapeCart(data.cartLinesAdd.cart);
}

export async function updateCart(
  cartId: string,
  lines: { id: string; quantity: number }[]
): Promise<Cart> {
  const query = `
    ${CART_FRAGMENT}
    mutation UpdateCart($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart {
          ...CartFragment
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const data = await shopifyFetch<CartLinesUpdateResponse>({
    query,
    variables: { cartId, lines },
    cache: 'no-store',
  });

  if (data.cartLinesUpdate.userErrors.length > 0) {
    throw new Error(data.cartLinesUpdate.userErrors[0].message);
  }

  return reshapeCart(data.cartLinesUpdate.cart);
}

export async function removeFromCart(cartId: string, lineIds: string[]): Promise<Cart> {
  const query = `
    ${CART_FRAGMENT}
    mutation RemoveFromCart($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart {
          ...CartFragment
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const data = await shopifyFetch<CartLinesRemoveResponse>({
    query,
    variables: { cartId, lineIds },
    cache: 'no-store',
  });

  if (data.cartLinesRemove.userErrors.length > 0) {
    throw new Error(data.cartLinesRemove.userErrors[0].message);
  }

  return reshapeCart(data.cartLinesRemove.cart);
}

export async function updateCartBuyerIdentity(
  cartId: string,
  buyerIdentity: {
    email?: string;
    phone?: string;
    countryCode?: string;
    deliveryAddressPreferences?: {
      deliveryAddress: {
        address1?: string;
        address2?: string;
        city?: string;
        company?: string;
        country?: string;
        firstName?: string;
        lastName?: string;
        phone?: string;
        province?: string;
        zip?: string;
      };
    }[];
  }
): Promise<Cart> {
  const query = `
    ${CART_FRAGMENT}
    mutation CartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
      cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
        cart {
          ...CartFragment
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const data = await shopifyFetch<{
    cartBuyerIdentityUpdate: {
      cart: ShopifyCart;
      userErrors: { field: string[]; message: string }[];
    };
  }>({
    query,
    variables: { cartId, buyerIdentity },
    cache: 'no-store',
  });

  if (data.cartBuyerIdentityUpdate.userErrors.length > 0) {
    throw new Error(data.cartBuyerIdentityUpdate.userErrors[0].message);
  }

  return reshapeCart(data.cartBuyerIdentityUpdate.cart);
}

export async function applyDiscountCode(
  cartId: string,
  discountCodes: string[]
): Promise<{ cart: Cart; userErrors: string[] }> {
  const query = `
    ${CART_FRAGMENT}
    mutation CartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]) {
      cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
        cart {
          ...CartFragment
          discountCodes {
            code
            applicable
          }
          discountAllocations {
            discountedAmount {
              amount
              currencyCode
            }
          }
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const data = await shopifyFetch<{
    cartDiscountCodesUpdate: {
      cart: ShopifyCart & {
        discountCodes: { code: string; applicable: boolean }[];
        discountAllocations: { discountedAmount: Money }[];
      };
      userErrors: { field: string[]; message: string }[];
    };
  }>({
    query,
    variables: { cartId, discountCodes },
    cache: 'no-store',
  });

  const userErrors = data.cartDiscountCodesUpdate.userErrors.map(e => e.message);

  return {
    cart: reshapeCart(data.cartDiscountCodesUpdate.cart),
    userErrors,
  };
}

export async function removeDiscountCodes(cartId: string): Promise<Cart> {
  const query = `
    ${CART_FRAGMENT}
    mutation CartDiscountCodesUpdate($cartId: ID!) {
      cartDiscountCodesUpdate(cartId: $cartId, discountCodes: []) {
        cart {
          ...CartFragment
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const data = await shopifyFetch<{
    cartDiscountCodesUpdate: {
      cart: ShopifyCart;
      userErrors: { field: string[]; message: string }[];
    };
  }>({
    query,
    variables: { cartId },
    cache: 'no-store',
  });

  return reshapeCart(data.cartDiscountCodesUpdate.cart);
}

export function formatPrice(money: Money): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: money.currencyCode,
  }).format(parseFloat(money.amount));
}

// ===========================================================================
// Customer accounts & orders
// ===========================================================================

const ADDRESS_FRAGMENT = `
  fragment AddressFragment on MailingAddress {
    id
    firstName
    lastName
    company
    address1
    address2
    city
    province
    provinceCode
    country
    countryCodeV2
    zip
    phone
    formatted
  }
`;

const ORDER_FRAGMENT = `
  fragment OrderFragment on Order {
    id
    orderNumber
    name
    processedAt
    fulfillmentStatus
    financialStatus
    currentTotalPrice {
      amount
      currencyCode
    }
    statusUrl
    shippingAddress {
      ...AddressFragment
    }
    lineItems(first: 50) {
      edges {
        node {
          title
          quantity
          variant {
            id
            title
            price {
              amount
              currencyCode
            }
            image {
              url
              altText
              width
              height
            }
            product {
              handle
            }
          }
        }
      }
    }
  }
`;

const CUSTOMER_FRAGMENT = `
  fragment CustomerFragment on Customer {
    id
    email
    firstName
    lastName
    displayName
    phone
    acceptsMarketing
    createdAt
    defaultAddress {
      ...AddressFragment
    }
    addresses(first: 10) {
      edges {
        node {
          ...AddressFragment
        }
      }
    }
    orders(first: 20, sortKey: PROCESSED_AT, reverse: true) {
      edges {
        node {
          ...OrderFragment
        }
      }
    }
  }
`;

function reshapeOrder(order: ShopifyOrder): Order {
  return {
    ...order,
    lineItems: order.lineItems.edges.map((edge) => edge.node) as OrderLineItem[],
  };
}

function reshapeCustomer(customer: ShopifyCustomer): Customer {
  return {
    ...customer,
    addresses: customer.addresses.edges.map((edge) => edge.node) as Address[],
    orders: customer.orders.edges.map((edge) => reshapeOrder(edge.node)) as Order[],
  };
}

function firstUserError(errors: CustomerUserError[] | undefined): string | null {
  if (!errors || errors.length === 0) return null;
  return errors[0].message;
}

export async function customerCreate(input: {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  acceptsMarketing?: boolean;
}): Promise<{ success: boolean; error: string | null }> {
  const query = `
    mutation CustomerCreate($input: CustomerCreateInput!) {
      customerCreate(input: $input) {
        customer { id email }
        customerUserErrors { code field message }
      }
    }
  `;

  const data = await shopifyFetch<CustomerCreateResponse>({
    query,
    variables: { input },
    cache: 'no-store',
  });

  const err = firstUserError(data.customerCreate.customerUserErrors);
  return { success: !err && !!data.customerCreate.customer, error: err };
}

export async function customerAccessTokenCreate(
  email: string,
  password: string
): Promise<{ token: CustomerAccessToken | null; error: string | null }> {
  const query = `
    mutation CustomerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
      customerAccessTokenCreate(input: $input) {
        customerAccessToken { accessToken expiresAt }
        customerUserErrors { code field message }
      }
    }
  `;

  const data = await shopifyFetch<CustomerAccessTokenCreateResponse>({
    query,
    variables: { input: { email, password } },
    cache: 'no-store',
  });

  const err = firstUserError(data.customerAccessTokenCreate.customerUserErrors);
  return {
    token: data.customerAccessTokenCreate.customerAccessToken,
    error: err,
  };
}

export async function customerAccessTokenDelete(token: string): Promise<void> {
  const query = `
    mutation CustomerAccessTokenDelete($customerAccessToken: String!) {
      customerAccessTokenDelete(customerAccessToken: $customerAccessToken) {
        deletedAccessToken
        userErrors { field message }
      }
    }
  `;
  await shopifyFetch({
    query,
    variables: { customerAccessToken: token },
    cache: 'no-store',
  });
}

export async function customerRecover(email: string): Promise<void> {
  const query = `
    mutation CustomerRecover($email: String!) {
      customerRecover(email: $email) {
        customerUserErrors { code field message }
      }
    }
  `;
  await shopifyFetch({
    query,
    variables: { email },
    cache: 'no-store',
  });
}

export async function customerResetByUrl(
  resetUrl: string,
  password: string
): Promise<{ token: CustomerAccessToken | null; error: string | null }> {
  const query = `
    mutation CustomerResetByUrl($resetUrl: URL!, $password: String!) {
      customerResetByUrl(resetUrl: $resetUrl, password: $password) {
        customerAccessToken { accessToken expiresAt }
        customerUserErrors { code field message }
      }
    }
  `;

  const data = await shopifyFetch<{
    customerResetByUrl: {
      customerAccessToken: CustomerAccessToken | null;
      customerUserErrors: CustomerUserError[];
    };
  }>({
    query,
    variables: { resetUrl, password },
    cache: 'no-store',
  });

  return {
    token: data.customerResetByUrl.customerAccessToken,
    error: firstUserError(data.customerResetByUrl.customerUserErrors),
  };
}

export async function getCustomer(accessToken: string): Promise<Customer | null> {
  const query = `
    ${ADDRESS_FRAGMENT}
    ${ORDER_FRAGMENT}
    ${CUSTOMER_FRAGMENT}
    query GetCustomer($customerAccessToken: String!) {
      customer(customerAccessToken: $customerAccessToken) {
        ...CustomerFragment
      }
    }
  `;

  try {
    const data = await shopifyFetch<CustomerResponse>({
      query,
      variables: { customerAccessToken: accessToken },
      cache: 'no-store',
    });
    return data.customer ? reshapeCustomer(data.customer) : null;
  } catch {
    return null;
  }
}
